package dev.kuml.portal.web.editor

import dev.kuml.portal.web.i18n.strings.EditorStrings

/**
 * Language-independent copy for the editor's collaboration-presence indicator (see
 * `EditorScreen.kt`'s `collabIndicatorDiv`) -- pure string formatting, DOM-free, mirroring
 * `usage/UsageFormat.kt`'s style so the singular/plural wording rule is directly unit-testable
 * (see `CollabPresenceFormatTest`).
 *
 * R-18: [indicatorText] now delegates the actual wording to [EditorStrings.collabIndicator]
 * (parameterised, see `GenerateState.kt`'s identical rationale) -- this object stays the single
 * call site the rest of the editor uses, so callers don't need to know the wording moved.
 */
public object CollabPresenceFormat {
    /**
     * `null` -- meaning "show nothing, no chip" -- when nobody else is connected (0 or fewer,
     * defensively). Singular wording for exactly one other connection, plural otherwise. No
     * cursor/selection detail, no avatars/names -- just a liveness count, matching the v1 scope.
     */
    public fun indicatorText(
        otherConnections: Int,
        strings: EditorStrings,
    ): String? = strings.collabIndicator(otherConnections)

    /** R-16: at most this many presence avatars are rendered side by side; anyone beyond that
     * collapses into [overflowText] instead of an ever-widening avatar strip. */
    public const val MAX_VISIBLE_AVATARS: Int = 5

    /** `null` if [totalPeers] fits within [MAX_VISIBLE_AVATARS] (nothing to collapse), otherwise
     * `"+N"` for the remainder -- deliberately language-neutral (a bare number), no translation
     * needed. */
    public fun overflowText(totalPeers: Int): String? {
        val overflow = totalPeers - MAX_VISIBLE_AVATARS
        return if (overflow > 0) "+$overflow" else null
    }

    /** R-28: the `title`/`aria-label` tooltip for one peer's avatar in `EditorScreen.kt`'s collab
     * avatar strip -- [elementName] is the name of the (first) element that peer currently has
     * selected, or `null` if they have nothing selected, mirroring [strings.collabPeerSelected]/
     * [strings.collabPeerPresent]'s own "with or without a selection" split. */
    public fun avatarTitle(
        displayName: String,
        elementName: String?,
        strings: EditorStrings,
    ): String =
        if (elementName != null) {
            strings.collabPeerSelected(displayName, elementName)
        } else {
            strings.collabPeerPresent(displayName)
        }
}
